import { ChangeDetectionStrategy, Component, inject, linkedSignal, OnInit, signal } from '@angular/core';

import { environment } from '../../../../environments/environment';
import { BaseSubResource, dataToken } from '../../../core/abstractions/base.sub-resource';
import { ApiResponseModel } from '../../../models/core/api-response.model';
import { TreeItemModel } from '../../../models/core/tree-item.model';
import { TreeNodeModel } from '../../../models/core/tree-node.model';
import { ReportModel } from '../../../models/report.model';
import { OptionalType } from '../../../models/types/optional.type';
import { AppSessionStorage } from '../../../services/storage/app.session-storage';
import { SecurityStorage } from '../../../services/storage/security.storage';

@Component({
    selector: 'sl-sub-reports',
    templateUrl: './sub-reports.component.html',
    styleUrls: ['./sub-reports.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SubReportsComponent extends BaseSubResource implements OnInit {
    readonly tree = signal<TreeItemModel[]>([]);
    readonly reportUrl = signal<OptionalType<string>>(undefined);
    readonly isReportLoaded = signal(false);

    readonly selectedGroup = linkedSignal(() => this.tree()[0]?.name || '');

    private context = '';
    private token = '';

    private readonly securityStorageService = inject(SecurityStorage);
    private readonly appSessionStorage = inject(AppSessionStorage);
    private readonly componentData = inject<ApiResponseModel<ReportModel[]>>(dataToken);

    constructor() {
        super();
        this.setupTree(this.componentData.value);
    }

    ngOnInit(): void {
        this.context = this.appSessionStorage.getCurrentContext();
        this.token = this.securityStorageService.getUserInfo()?.token ?? '';
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    hasChild(_: number, node: TreeNodeModel): boolean {
        return node.isExpandable;
    }

    loadReport(uri: string): void {
        this.isReportLoaded.set(false);
        this.reportUrl.set(`${environment.reportingUrl}/${uri}&api_key=${this.token}&ctx=${this.context}`);
    }

    private setupTree(items: ReportModel[]): void {
        const names = items.map(r => r.group);
        const distinctGroupNames = [...new Set(names)].sort();

        const tree: TreeItemModel[] = [];
        distinctGroupNames.forEach(n => tree.push(new TreeItemModel({ name: n })));
        items.forEach(item => {
            const group = tree.find(g => g.name === item.group);
            if (!group) {
                return;
            }
            group.children.push(new TreeItemModel({ name: item.description, id: item.uri }));
        });
        this.tree.set(tree);
    }
}
